// Modules
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// Rxjs
import { of } from 'rxjs';
import { map, exhaustMap, catchError, tap } from 'rxjs/operators';

// Constants
import { APP_PATHS_CONSTANTS } from '@shared/constants';

// Store - Actions
import * as ForcedPasswordChangeActions from '@store/actions/forced-password-change.actions';
import * as AuthActions from '@store/actions/auth.actions';

// Store - State
import { AppState } from '@store/reducers';

// Services
import { ForcedPasswordChangeService } from '@services/forced-password-change-service/forced-password-change.service';
import { AppConfigService } from '@shared-module/services/app-config/app-config.service';

// Toast
import { SbiToastService } from '@shared-module/services/sbi-toast/sbi-toast.service';

@Injectable({
  providedIn: 'root',
})
export class ForcedPasswordChangeEffects {
  constructor(
    private _actions$: Actions,
    private _router: Router,
    private _store: Store<AppState>,
    private _app: AppConfigService,
    private _service: ForcedPasswordChangeService,
    private _sbiToast: SbiToastService
  ) {}

  // Different auth endpoints return the validation message differently —
  // some as a plain-text body, some as JSON ({ errorMessage: string }).
  // Support both so we don't hardcode one shape per endpoint.
  private _extractFieldError(err: any): string {
    // Plain-text body returned directly as the error.
    if (typeof err?.error === 'string') {
      return err.error.trim();
    }
    // Some endpoints reply with plain text while HttpClient expects JSON; the
    // raw body then lands in err.error.text after the parse failure.
    if (typeof err?.error?.text === 'string') {
      return err.error.text.trim();
    }
    // JSON-shaped error ({ errorMessage: string }).
    if (typeof err?.error?.errorMessage === 'string') {
      return err.error.errorMessage.trim();
    }
    return '';
  }

  // Handle password change request
  changePassword$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ForcedPasswordChangeActions.forcedPasswordChangeRequested),
      exhaustMap(action =>
        this._service
          .changePassword(
            action.payload.challengeId,
            action.payload.newPassword
          )
          .pipe(
            map(response => {
              // The response IS the full login object (same as normal login)
              // Build the user object from it
              const user = {
                email: response.email,
                accessToken:
                  response.accessToken?.token || response.accessToken,
                refreshToken: response.refreshToken,
              };
              return ForcedPasswordChangeActions.forcedPasswordChangeSuccess({
                payload: { user },
              });
            }),
            catchError(err => {
              const body = this._extractFieldError(err);

              if (body === 'token_expired' || body === 'token_invalid') {
                return of(
                  ForcedPasswordChangeActions.forcedPasswordChangeFailed({
                    payload: {
                      toastMessage:
                        'La sesión ha expirado. Iniciá sesión nuevamente.',
                      sessionExpired: true,
                    },
                  })
                );
              }

              // Any other 400 is a password validation message meant for the
              // field (e.g. same-as-previous, contains name/document/email) —
              // shown as-is under the input, never as a toast.
              if (err.status === 400 && body) {
                return of(
                  ForcedPasswordChangeActions.forcedPasswordChangeFailed({
                    payload: { fieldError: body },
                  })
                );
              }

              return of(
                ForcedPasswordChangeActions.forcedPasswordChangeFailed({
                  payload: {
                    toastMessage:
                      'No pudimos actualizar la contraseña. Intentá nuevamente.',
                  },
                })
              );
            })
          )
      )
    )
  );

  // On success: save user to sessionStorage + navigate to private area
  changeSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ForcedPasswordChangeActions.forcedPasswordChangeSuccess),
      tap(action => {
        // The action carries the full user object — save it directly to sessionStorage
        sessionStorage.setItem(
          this._app.loggedUserSessionKey,
          JSON.stringify(action.payload.user)
        );
        // Dispatch loginSuccessful to populate auth store (needed for AuthGuard)
        this._store.dispatch(
          AuthActions.loginSuccessful({ payload: action.payload.user })
        );
        this._sbiToast.success('La contraseña se actualizó correctamente');
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
      }),
      map(() => ForcedPasswordChangeActions.forcedPasswordChangeCleared())
    )
  );

  // On failure: toast only for generic/session errors — field errors are
  // rendered by the component from the store, never as a toast.
  changeFailed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ForcedPasswordChangeActions.forcedPasswordChangeFailed),
        tap(action => {
          if (action.payload.toastMessage) {
            this._sbiToast.error(action.payload.toastMessage);
          }
          if (action.payload.sessionExpired) {
            this._store.dispatch(
              ForcedPasswordChangeActions.forcedPasswordChangeCleared()
            );
            this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_LOGIN);
          }
        })
      ),
    { dispatch: false }
  );
}
